import React, { useCallback, useEffect, useMemo } from 'react';
import { Box, Card, CardContent, Grid, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import util from '../util';
import strings from '../Globalization';
import NextPrevComponent from '../components/common/NextPrevComponent';
import consts from '../consts';
import CycleInfoProvider from '../contexts/CycleInfo';
import { useCycleInfo } from '../contexts/CycleInfo/Context';
import CycleTape from '../contexts/CycleInfo/CycleTape';
import TrendsLineChart from '../contexts/CycleInfo/TrendsLineChart';
import { usePrerequisites } from '../contexts/Prerequisites';
import { useSetState, useUniqueEffect } from '../hooks';
import Connection from '../services/Connection';
import CriticalParametersTable from './cycleinfoviews/CriticalParametersTable';
import FlowCheckTable from './cycleinfoviews/FlowCheckTable';
import ProcessMonitor from './cycleinfoviews/ProcessMonitor';
import TopSection from './cycleinfoviews/TopSection';
import { getPrintDetailsParams } from './cycleinfoviews/dataUtil';

export const CycleInfo = () => {
	const [, { navigateToLastAccessibleRoute }] = usePrerequisites();
	const { data, cycleTapeDisplayed, onNext, onPrevious, stockChartRef } = useCycleInfo();
	const equipmentDetail = data && data.ChartDetail ? data.ChartDetail?.EQUIPMENTDETAIL : '';
	const isPerformanceAccess = useMemo(() => util.checkAccess('PerformanceMonitorAccess'), []);
	const theme = useTheme();
	let displayCycleTape = false;
	const screenWidth = useMediaQuery(theme.breakpoints.between('sm', 'md'));
	const is1024Width = useMediaQuery('(max-width: 1024px) and (max-height: 768px)');
	if (screenWidth || is1024Width) {
		displayCycleTape = true;
	}
	useEffect(() => {
		if (!isPerformanceAccess) {
			Swal.fire({
				titleText: strings.Alert,
				text: strings.AccessError,
				showCancelButton: false,
				confirmButtonText: strings.Ok,
				confirmButtonClass: 'btn-danger',
				closeOnConfirm: true,
				allowOutsideClick: false
			}).then(() => {
				navigateToLastAccessibleRoute();
			});
		}
	}, [isPerformanceAccess]);
	const isPhasesDataAvailable = data?.ChartDetail?.isPhasesDataAvailable;
	return (
		!!data && (
			<Grid container rowSpacing={2} size={12}>
				{(
					<Grid size={12} container>
						<Card
							sx={{
								width: '100%',
								'& .MuiCardContent-root:last-child': {
									paddingBottom: '0px',
								}
							}}
						>
							<CardContent>
								<Grid container size={12}>
									<Grid size={12} >
										<NextPrevComponent onNext={onNext} onPrev={onPrevious}>
											<Box display="flex" columnGap={1} flexDirection={'row'}>
												<Box flexGrow={1}>
													<TopSection />
												</Box>
											</Box>
										</NextPrevComponent>
									</Grid>
								</Grid>
							</CardContent>
						</Card>
					</Grid>
				)}
				<Grid size={12} container>
					<Card sx={{ width: '100%' }}>
						<CardContent>

							<Grid container>
								<Grid size={12}>
									<Box
										sx={{
											display: 'grid',
											gridTemplateColumns: displayCycleTape ? '1fr' : '1fr auto',
											gridTemplateRows: '1fr',
											gridColumnGap: '20px'
										}}
									>
										<Box>
											<Grid container flexDirection="column" rowGap={3}>
												<Grid size={12}>
													<FlowCheckTable />
												</Grid>
												<Grid size={12}>
													<CriticalParametersTable />
												</Grid>

												<Grid size={12}>
													{isPhasesDataAvailable ? (
														<Box paddingTop={2}>
															<Typography variant="subtitle1" fontWeight={'bold'}>
																{strings.PRESSURELOSSLEAKANDFLOWCHECKTEMPERATURE}
															</Typography>
															<TrendsLineChart ref={stockChartRef} />
														</Box>
													) : (
														<Box paddingTop={2}>
															<Typography
																variant="subtitle1"
																color={'textSecondary'}
																fontWeight={'bold'}
																textAlign={'center'}
															>
																{strings.NoChartDataAvailableForCycle}
															</Typography>
														</Box>
													)}
												</Grid>
											</Grid>
										</Box>
										{cycleTapeDisplayed && (
											<Box>
												<CycleTape />
											</Box>
										)}
									</Box>
								</Grid>
							</Grid>
						</CardContent>
					</Card>
				</Grid>
			</Grid>
		)
	);
};



const DefaultCycleInfo = ({ comboTypes = ['CycleActionStatus', 'CleaningIndicator'] }) => {
	const [, { navigateToLastAccessibleRoute }] = usePrerequisites();
	const dateTimeFormat = util.getDateTimeFormat();
	const [searchParams, setSearchParams] = useSearchParams();
	const canReprocessXml = true;
	const isPerformanceAccess = useMemo(() => util.checkAccess('PerformanceMonitorAccess'), []);
	const view = useMemo(() => {
		let v = searchParams.get('view');
		if (!['processMonitor', 'cycleDetails'].includes(v)) {
			v = 'cycleDetails';
		}
		return v;
	}, [searchParams]);
	const [state, setState] = useSetState({
		data: null,
		equipmentDetail: null
	});
	const equipmentFamilyId = useMemo(() => searchParams.get('equipmentFamilyId'), [searchParams, state.data]);
	const isEndoWasher = useMemo(
		() => (state.data?.EquipmentTypeId || searchParams.get('isEndoWasher')) === consts.EquipmentType.ENDOWASHER,
		[searchParams, state.data]
	);
	const isUltraSonic = useMemo(
		() => (state.data?.EquipmentTypeId || searchParams.get('isUltraSonic')) === consts.EquipmentType.ULTRASONIC,
		[searchParams, state.data]
	);
	const isWasher = useMemo(
		() => (state.data?.EquipmentTypeId || searchParams.get('isWasher')) === consts.EquipmentType.WASHER,
		[searchParams, state.data]
	);

	const onTabChange = (tab) => setState({ tab });



	const onChangeView = useCallback(() => {
		const v = view === 'processMonitor' ? 'cycleDetails' : 'processMonitor';
		const searchParamsObj = {};
		for (const [key, value] of searchParams.entries()) {
			searchParamsObj[key] = value;
		}
		searchParamsObj.view = v;
		setSearchParams(searchParamsObj);
	}, [searchParams, view]);

	const onGetPrintDetailsParams = useCallback(
		({ data }) =>
			getPrintDetailsParams({
				data,
				dateTimeFormat,
			}),
		[dateTimeFormat]
	);

	useUniqueEffect(() => {
		if (!isPerformanceAccess) {
			Swal.fire({
				titleText: strings.Alert,
				text: strings.AccessError,
				showCancelButton: false,
				confirmButtonText: strings.Ok,
				confirmButtonClass: 'btn-danger',
				closeOnConfirm: true,
				allowOutsideClick: false
			}).then(() => {
				navigateToLastAccessibleRoute(false);
			});
		}
	}, [isPerformanceAccess]);

	const breadcrumbsProps = useMemo(
		() => ({
			startActions: isEndoWasher
				? [
					{
						color: 'primary',
						disableElevation: true,
						text: view === 'processMonitor' ? strings.CycleLogs : strings.ProcessMonitor,
						variant: 'contained',
						size: 'small',
						onClick: onChangeView
					}
				]
				: []
		}),
		[setState, view, onChangeView, isEndoWasher]
	);

	const onDataLoad = ({ data }) => {
		setState({ data, equipmentDetail: !!data?.ChartDetail?.EQUIPMENTDETAIL })
	};

	const onFetchCIResultFormData = ({ data: cycle }) => {
		const isWasher = cycle?.EquipmentTypeId === consts.EquipmentType.WASHER;
		const isUltraSonic = cycle?.EquipmentTypeId === consts.EquipmentType.ULTRASONIC;
		const isEndoWasher = cycle?.ConnectivityTypeId === consts.ConnectivityType.Kodiak;
		const comboTypes = [{ type: 'CleaningIndicator', loaded: true }];
		const formatData = new FormData();

		formatData.append('attachmentCount', cycle?.AttachmentCount ?? 0);
		formatData.append('id', cycle?._id ?? cycle?.Id);
		formatData.append('isEndoWasher', isEndoWasher);
		formatData.append('isUltraSonic', isUltraSonic);
		formatData.append('isWasher', isWasher);
		formatData.append('action', 'load');
		formatData.append('comboTypes', JSON.stringify(comboTypes));
		formatData.append('WasherCIResult', cycle?.WasherCIResult);
		formatData.append('WasherCILotNumberId', cycle?.WasherCILotNumberId || null);

		return fetch(Connection.CycleList, { method: 'POST', body: formatData })
			.then((res) => res.json())
			.then(({ data, combos }) => {
				combos.CleaningIndicator = Array.isArray(combos.CleaningIndicator)
					? combos.CleaningIndicator.map((entry) => {
						if (typeof entry === 'object' && 'ExpiryDate' in entry) {
							entry.ExpiryDate = entry.ExpiryDate ? util.fixDates(entry.ExpiryDate) : entry.ExpiryDate;
						}
						return entry;
					})
					: [];
				return { combos };
			}).catch(error => {
				console.error('Error CI Result form data loading data', error);
				return { combos: [] }
			});
	};

	return (
		<CycleInfoProvider
			value={{
				...state,
				type: isWasher ? 'Washer' : isEndoWasher ? 'EndoWasher' : 'UltraSonic',
				view,
				isEndoWasher,
				isUltraSonic,
				isWasher,
				onTabChange,
				onChangeView,
				canReprocessXml,
				equipmentFamilyId,
			}}
			type={state.type}
			onGetPrintDetailsParams={onGetPrintDetailsParams}
			dataUrl={Connection.CycleList}
			breadcrumbsProps={breadcrumbsProps}
			onDataLoad={onDataLoad}
			onFetchCIResultFormData={onFetchCIResultFormData}
		>
			{view === 'processMonitor' && <ProcessMonitor />}
			{view === 'cycleDetails' && <CycleInfo />}
		</CycleInfoProvider>
	);
};
export default DefaultCycleInfo;
